/**
 * @fileoverview ClubOS Budget Planning page — FY 2025-26
 * Department budget allocations vs actual spend.
 * All values in INR. TODO: Replace with financeService.getBudget()
 */

import { useState, useMemo, useEffect, useCallback } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftProgress from "components/SoftProgress";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";

// ClubOS utilities & data
import { formatCompact, formatDateIN, CLUB_NAME } from "utils/indiaLocale";
import { financialSummary } from "data/mockData";
import { PermissionGate } from "components/PermissionGate";
import { financeService } from "api";
import { useAuth } from "context/AuthContext";

// ── API → UI shape mapper ────────────────────────────────────────────────────
function mapApiBudget(dto) {
  const pct = dto.utilisationPercent;
  const status = pct > 100 ? "Over Budget" : pct < 50 ? "Under Budget" : "On Track";
  return {
    id:           dto.id,
    department:   dto.department,
    fiscalYear:   dto.fiscalYear,
    annualBudget: dto.allocatedAmount,
    ytdSpent:     dto.spentAmount,
    notes:        dto.notes,
    status,
  };
}

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────

/** Annual budget totals */
const ANNUAL_BUDGET     = 95_00_00_000;  // ₹95 Cr total
const ANNUAL_ALLOCATED  = 88_00_00_000;  // ₹88 Cr allocated
const ANNUAL_SPENT      = 64_00_00_000;  // ₹64 Cr YTD spent
const ANNUAL_REMAINING  = ANNUAL_BUDGET - ANNUAL_SPENT;

/** Status badge colour */
const STATUS_COLORS = {
  "On Track":    "success",
  "Over Budget": "error",
  "Under Budget":"warning",
};

/** Department names for dropdown (fallback list) */
const DEPARTMENTS_FALLBACK = [
  "Player Wages", "Transfer Fees (Amortised)", "Coaching Staff", "Medical & Fitness",
  "Travel & Accommodation", "Infrastructure & Facilities", "Youth Academy",
  "Marketing & Media", "Administration", "Scouting Operations",
];

/** Empty budget entry form */
const EMPTY_ENTRY_FORM = {
  department: "",
  description: "",
  amount: "",
  date: "",
  notes: "",
};

/** Budget vs Actual monthly chart — ISL season Sep–Mar */
const monthlyChart = {
  labels: financialSummary.budgetVsActual.map((d) => d.month),
  datasets: [
    {
      label: "Budget",
      data: financialSummary.budgetVsActual.map((d) => parseFloat((d.budget / 1_00_00_000).toFixed(2))),
      color: "info",
    },
    {
      label: "Actual",
      data: financialSummary.budgetVsActual.map((d) => parseFloat((d.actual / 1_00_00_000).toFixed(2))),
      color: "dark",
    },
  ],
};

// ─── COMPONENT ─────────────────────────────────────────────────────────────────

function BudgetPage() {
  const { currentUser } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBudgets = useCallback(() => {
    setLoading(true);
    financeService.getDepartmentBudgets()
      .then((data) => setBudgets(data.map(mapApiBudget)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchBudgets(); }, [fetchBudgets]);

  const DEPARTMENTS = budgets.length > 0
    ? budgets.map((d) => d.department)
    : DEPARTMENTS_FALLBACK;

  // Revise Budget dialog
  const [reviseBudgetOpen, setReviseBudgetOpen] = useState(false);
  const [revisedBudgets, setRevisedBudgets] = useState({});

  // Add Entry dialog
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [entryForm, setEntryForm] = useState(EMPTY_ENTRY_FORM);

  // ── Computed totals ───────────────────────────────────────────────────────
  const totalAllocated = useMemo(
    () => budgets.reduce((sum, d) => sum + d.annualBudget, 0),
    [budgets]
  );
  const totalSpent = useMemo(
    () => budgets.reduce((sum, d) => sum + d.ytdSpent, 0),
    [budgets]
  );
  const overallPct = Math.round((totalSpent / ANNUAL_BUDGET) * 100);

  // ── Revise budget handlers ────────────────────────────────────────────────
  const openRevise = () => {
    const initialRevised = {};
    budgets.forEach((d) => {
      initialRevised[d.id] = d.annualBudget;
    });
    setRevisedBudgets(initialRevised);
    setReviseBudgetOpen(true);
  };

  const handleReviseChange = (id, value) => {
    setRevisedBudgets((prev) => ({ ...prev, [id]: Number(value) }));
  };

  const handleReviseConfirm = async () => {
    try {
      const saves = budgets
        .filter((d) => revisedBudgets[d.id] !== undefined)
        .map((d) =>
          financeService.upsertDepartmentBudget({
            id:              d.id,
            department:      d.department,
            fiscalYear:      d.fiscalYear ?? new Date().getFullYear(),
            allocatedAmount: revisedBudgets[d.id] ?? d.annualBudget,
            notes:           d.notes ?? null,
            tenantId:        currentUser.tenantId,
          })
        );
      await Promise.all(saves);
      await fetchBudgets();
    } catch (err) {
      alert(err?.title ?? err?.message ?? "Failed to save budgets");
    }
    setReviseBudgetOpen(false);
  };

  // ── Add Entry handlers ────────────────────────────────────────────────────
  const handleEntryFormChange = (field, value) => {
    setEntryForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddEntry = async () => {
    if (!entryForm.department || !entryForm.amount) return;
    try {
      await financeService.createRecord({
        description:     entryForm.description || entryForm.department,
        amount:          Number(entryForm.amount),
        currency:        "INR",
        category:        "Expenditure",
        costCentre:      entryForm.department,
        department:      entryForm.department,
        transactionDate: entryForm.date || new Date().toISOString().slice(0, 10),
        notes:           entryForm.notes || null,
        tenantId:        currentUser.tenantId,
      });
      await fetchBudgets();
    } catch (err) {
      alert(err?.title ?? err?.message ?? "Failed to add entry");
    }
    setAddEntryOpen(false);
    setEntryForm(EMPTY_ENTRY_FORM);
  };

  const handleDeleteBudget = async (budgetId) => {
    if (!window.confirm("Delete this budget allocation?")) return;
    try {
      await financeService.deleteDepartmentBudget(budgetId);
      setBudgets((prev) => prev.filter((b) => b.id !== budgetId));
    } catch (err) {
      alert(err?.title ?? err?.message ?? "Failed to delete budget");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              Budget Planning
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              FY 2025-26 &middot; {CLUB_NAME} &middot; ISL Season 2025/26
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <PermissionGate permission="finance.budget.edit">
              <SoftButton variant="outlined" color="warning" onClick={openRevise}>
                <Icon sx={{ mr: 0.5 }}>edit</Icon>
                Revise Budget
              </SoftButton>
            </PermissionGate>
            <PermissionGate permission="finance.budget.edit">
              <SoftButton variant="gradient" color="info" onClick={() => setAddEntryOpen(true)}>
                <Icon sx={{ mr: 0.5 }}>add</Icon>
                Add Entry
              </SoftButton>
            </PermissionGate>
          </SoftBox>
        </SoftBox>

        {/* ── KPI Cards ───────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Budget FY26" }}
                count={formatCompact(ANNUAL_BUDGET)}
                percentage={{ color: "info", text: "annual allocation" }}
                icon={{ color: "info", component: "account_balance" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Allocated" }}
                count={formatCompact(ANNUAL_ALLOCATED)}
                percentage={{ color: "warning", text: `${Math.round((ANNUAL_ALLOCATED / ANNUAL_BUDGET) * 100)}% of total` }}
                icon={{ color: "warning", component: "assignment" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "YTD Spent" }}
                count={formatCompact(ANNUAL_SPENT)}
                percentage={{ color: "error", text: `${overallPct}% of budget` }}
                icon={{ color: "error", component: "payments" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Remaining" }}
                count={formatCompact(ANNUAL_REMAINING)}
                percentage={{ color: "success", text: "unspent this FY" }}
                icon={{ color: "success", component: "savings" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Overall utilisation bar ──────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <SoftTypography variant="h6" fontWeight="medium">Annual Budget Overview</SoftTypography>
                <SoftBadge
                  badgeContent={`${overallPct}% utilised`}
                  color={overallPct > 90 ? "error" : overallPct > 70 ? "warning" : "success"}
                  size="xs"
                  container
                />
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                <SoftTypography variant="caption" color="text">YTD Spend vs Annual Budget</SoftTypography>
                <SoftTypography variant="caption" fontWeight="medium">
                  {formatCompact(ANNUAL_SPENT)} / {formatCompact(ANNUAL_BUDGET)}
                </SoftTypography>
              </SoftBox>
              <SoftProgress
                value={overallPct}
                color={overallPct > 90 ? "error" : overallPct > 70 ? "warning" : "success"}
                label
              />
            </SoftBox>
          </Card>
        </SoftBox>

        {/* ── Department Budget Table ──────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                Department Budget vs Actuals
              </SoftTypography>

              {loading ? (
                <SoftTypography variant="caption" color="text">Loading budgets...</SoftTypography>
              ) : (
                <SoftBox sx={{ overflowX: "auto" }}>
                  <SoftBox
                    component="table"
                    sx={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}
                  >
                    <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                      <SoftBox component="tr">
                        {["Department", "Annual Budget", "YTD Spent", "Remaining", "% Used", "Status"].map((h) => (
                          <SoftBox
                            key={h}
                            component="th"
                            sx={{
                              textAlign: "left",
                              p: 1.5,
                              color: "text.secondary",
                              fontWeight: 700,
                              fontSize: "0.7rem",
                              textTransform: "uppercase",
                              borderBottom: "2px solid #e9ecef",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </SoftBox>
                        ))}
                      </SoftBox>
                    </SoftBox>
                    <SoftBox component="tbody">
                      {budgets.map((dept, i) => {
                        const remaining = dept.annualBudget - dept.ytdSpent;
                        const pct = Math.round((dept.ytdSpent / dept.annualBudget) * 100);
                        const statusColor = STATUS_COLORS[dept.status] || "info";
                        return (
                          <SoftBox
                            key={dept.id}
                            component="tr"
                            sx={{
                              "&:hover": { backgroundColor: "#f8f9fa" },
                              backgroundColor: i % 2 === 0 ? "white" : "#fdfdfd",
                            }}
                          >
                            <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                              <SoftTypography variant="button" fontWeight="medium">{dept.department}</SoftTypography>
                            </SoftBox>
                            <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                              <SoftTypography variant="caption" fontWeight="medium">{formatCompact(dept.annualBudget)}</SoftTypography>
                            </SoftBox>
                            <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                              <SoftTypography variant="caption" color="error" fontWeight="medium">{formatCompact(dept.ytdSpent)}</SoftTypography>
                            </SoftBox>
                            <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="medium"
                                color={remaining < 0 ? "error" : "success"}
                              >
                                {remaining < 0 ? "-" : ""}{formatCompact(Math.abs(remaining))}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", minWidth: 120 }}>
                              <SoftBox display="flex" alignItems="center" gap={1}>
                                <SoftBox flex={1}>
                                  <SoftProgress value={Math.min(pct, 100)} color={statusColor} />
                                </SoftBox>
                                <SoftTypography variant="caption" color="text" sx={{ minWidth: 30 }}>
                                  {pct}%
                                </SoftTypography>
                              </SoftBox>
                            </SoftBox>
                            <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                              <SoftBadge
                                badgeContent={dept.status}
                                color={statusColor}
                                size="xs"
                                container
                              />
                            </SoftBox>
                          </SoftBox>
                        );
                      })}
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
          </Card>
        </SoftBox>

        {/* ── Monthly Budget vs Actual Chart ───────────────────────────────── */}
        <VerticalBarChart
          title="Monthly Budget vs Actuals (₹ Crores)"
          description="ISL season Sep–Mar — all expenditure categories combined"
          chart={monthlyChart}
          height="16rem"
        />
      </SoftBox>

      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />

      {/* ── Revise Budget Dialog ─────────────────────────────────────────────── */}
      <PermissionGate permission="finance.budget.edit">
        <Dialog open={reviseBudgetOpen} onClose={() => setReviseBudgetOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>Revise Department Budgets</DialogTitle>
          <DialogContent dividers>
            <SoftTypography variant="caption" color="text" display="block" mb={2}>
              Update annual budget allocations for FY 2025-26. Changes will affect FFP calculations.
            </SoftTypography>
            <Grid container spacing={2}>
              {budgets.map((dept) => (
                <Grid key={dept.id} item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={dept.department}
                    type="number"
                    value={revisedBudgets[dept.id] ?? dept.annualBudget}
                    onChange={(e) => handleReviseChange(dept.id, e.target.value)}
                    size="small"
                    inputProps={{ min: 0 }}
                    helperText={formatCompact(revisedBudgets[dept.id] ?? dept.annualBudget)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <SoftButton variant="outlined" color="secondary" onClick={() => setReviseBudgetOpen(false)}>
              Cancel
            </SoftButton>
            <SoftButton variant="gradient" color="warning" onClick={handleReviseConfirm}>
              Apply Revisions
            </SoftButton>
          </DialogActions>
        </Dialog>
      </PermissionGate>

      {/* ── Add Budget Entry Dialog ───────────────────────────────────────────── */}
      <PermissionGate permission="finance.budget.edit">
        <Dialog open={addEntryOpen} onClose={() => setAddEntryOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>Add Budget Entry</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} pt={1}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel shrink>Department</InputLabel>
                  <Select
                    value={entryForm.department}
                    label="Department"
                    onChange={(e) => handleEntryFormChange("department", e.target.value)}
                  >
                    {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Amount (₹)"
                  required
                  type="number"
                  value={entryForm.amount}
                  onChange={(e) => handleEntryFormChange("amount", e.target.value)}
                  size="small"
                  inputProps={{ min: 0 }}
                  helperText={entryForm.amount ? formatCompact(Number(entryForm.amount)) : ""}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  required
                  value={entryForm.description}
                  onChange={(e) => handleEntryFormChange("description", e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Date"
                  required
                  type="date"
                  value={entryForm.date}
                  onChange={(e) => handleEntryFormChange("date", e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={entryForm.notes}
                  onChange={(e) => handleEntryFormChange("notes", e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <SoftButton variant="outlined" color="secondary" onClick={() => setAddEntryOpen(false)}>
              Cancel
            </SoftButton>
            <SoftButton variant="gradient" color="info" onClick={handleAddEntry}>
              Add Entry
            </SoftButton>
          </DialogActions>
        </Dialog>
      </PermissionGate>
    </DashboardLayout>
  );
}

export default BudgetPage;
